import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, MenuItem, Paper, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
	DataGrid,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { roleAccess } from "../EboPaymentWorking";

const ReportToolbar = () => (
	<GridToolbarContainer>
		<GridToolbarColumnsButton />
		<GridToolbarFilterButton />
		<GridToolbarDensitySelector />
		<Box sx={{ flexGrow: 1 }} />
		<GridToolbarQuickFilter />
	</GridToolbarContainer>
);

const LEGACY_FRANCHISE_KEY_MAP = {
	shivera_lifestyle_private_limited: "SHIVERA LIFESTYLE PRIVATE LIMITED",
	rene_fashion: "RENE FASHION",
	shiv_shanti_garments: "SHIV SHANTI GARMENTS",
	kj_lifestyle_llp_ap: "K J LIFESTYLE LLP AP",
	kj_lifestyle_llp_kl: "K J LIFESTYLE LLP KL",
	kj_lifestyle_llp_ts: "K J LIFESTYLE LLP TS",
};

const getResponseRows = (data) => {
	if (Array.isArray(data)) return data;
	if (Array.isArray(data?.data)) return data.data;
	if (Array.isArray(data?.result)) return data.result;
	if (Array.isArray(data?.results)) return data.results;
	if (Array.isArray(data?.rows)) return data.rows;
	return [];
};

const formatHeaderName = (field) =>
	String(field || "")
		.replace(/_/g, " ")
		.replace(/([a-z])([A-Z])/g, "$1 $2")
		.trim()
		.toUpperCase();

const getMappedFranchiseName = (row) => row?.name ?? row?.franchise_name ?? row?.franchise ?? row?.label ?? "";
const getStoreName = (row) =>
	row?.store_display_name || row?.store_name || row?.STORE_NAME || row?.franchise_store_name || row?.short_code || row?.name || "";
const getStoreCode = (row) => row?.store_code || row?.code || row?.storeCode || row?.short_code || row?.SHORT_CODE || "";
const getStoreStatus = (row) => String(row?.store_status || "enabled").toLowerCase();

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const formatSelectedMonth = (selectedMonth) => (dayjs(selectedMonth).isValid() ? dayjs(selectedMonth).format("MMM YYYY") : "");

const buildSalaryRows = (rows, selectedMonth, franchiseLabel) =>
	rows.map((row, index) => {
		const storeName = getStoreName(row) || `Store ${index + 1}`;
		return {
			id: row?.id ?? row?._id ?? `salary-${storeName}-${index + 1}`,
			month: row?.month || formatSelectedMonth(selectedMonth),
			franchise_name: row?.franchise_name || franchiseLabel,
			store_name: storeName,
			store_code: getStoreCode(row),
			earned_salary: row?.earned_salary ?? row?.gross_salary ?? "",
			incentive: row?.incentive ?? "",
			employer_esi: row?.employer_esi ?? "",
			employer_pf: row?.employer_pf ?? "",
			total_liability: row?.total_liability ?? "",
			updated_by: row?.updated_by ?? "",
			created_date: row?.created_date ?? "",
		};
	});

const buildSalaryTemplateRows = (activeFranchise) => {
	if (activeFranchise) {
		return getResponseRows(activeFranchise?.stores)
			.filter((row) => getStoreStatus(row) === "enabled")
			.map((row) => ({
			franchise_name: activeFranchise?.franchise_name || "",
			store_name: getStoreName(row),
			store_code: getStoreCode(row),
			earned_salary: "",
			incentive: "",
			employer_esi: "",
			employer_pf: "",
			total_liability: "",
		}));
	}

	return getResponseRows(activeFranchise?.stores).map((store) => ({
		franchise_name: activeFranchise?.label || "",
		store_name: getStoreName(store),
		store_code: getStoreCode(store),
		earned_salary: "",
		incentive: "",
		employer_esi: "",
		employer_pf: "",
		total_liability: "",
	}));
};

const buildPettyCashRows = (rows, selectedMonth, franchiseLabel) =>
	rows.map((row, index) => {
		const storeName = getStoreName(row) || `Store ${index + 1}`;
		return {
			id: row?.id ?? row?._id ?? `petty-${storeName}-${index + 1}`,
			month: row?.month || formatSelectedMonth(selectedMonth),
			franchise_name: row?.franchise_name || franchiseLabel,
			store_name: storeName,
			store_code: getStoreCode(row),
			amount: row?.amount ?? "",
			updated_by: row?.updated_by ?? "",
			created_date: row?.created_date ?? "",
		};
	});

const buildPettyCashTemplateRows = (activeFranchise) => {
	if (activeFranchise) {
		return getResponseRows(activeFranchise?.stores)
			.filter((row) => getStoreStatus(row) === "enabled")
			.map((row) => ({
			franchise_name: activeFranchise?.franchise_name || "",
			store_name: getStoreName(row),
			store_code: getStoreCode(row),
			amount: "",
		}));
	}

	return getResponseRows(activeFranchise?.stores).map((store) => ({
		franchise_name: activeFranchise?.label || "",
		store_name: getStoreName(store),
		store_code: getStoreCode(store),
		amount: "",
	}));
};

const buildColumnsFromRows = (rows = []) => {
	const firstRow = rows[0];
	if (!firstRow) return [];

	return Object.keys(firstRow)
		.filter((field) => field !== "id")
		.map((field) => ({
			field,
			headerName: formatHeaderName(field),
			minWidth: 150,
			flex: 1,
		}));
};

const downloadCsv = (rows, fileName, headers) => {
	const csvRows = [
		headers.join(","),
		...rows.map((row) =>
			headers
				.map((header) => {
					const value = row?.[header] ?? "";
					const escaped = String(value).replace(/"/g, '""');
					return `"${escaped}"`;
				})
				.join(",")
		),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const CsvActionsCard = ({
	title,
	description,
	downloadTitle,
	downloadDescription,
	downloadLabel,
	onDownload,
	uploadTitle,
	uploadDescription,
	uploadLabel,
	onUpload,
	onFileSelect,
	selectedFileName,
	uploadDisabled,
	downloadDisabled,
	readOnlyUploadDisabled,
}) => (
	<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
		<Stack spacing={1.5}>
			<Box>
				<Typography variant="subtitle1" fontWeight={700}>
					{title}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
					{description}
				</Typography>
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
					gap: 1.5,
				}}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(59,130,246,0.03)" }}>
					<Stack spacing={1.25}>
						<Box>
							<Typography variant="subtitle2" fontWeight={700}>
								{downloadTitle}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{downloadDescription}
							</Typography>
						</Box>
						<Button
							variant="outlined"
							startIcon={<FileDownloadIcon />}
							onClick={onDownload}
							disabled={downloadDisabled ||readOnlyUploadDisabled}
							size="small"
							sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
							{downloadLabel}
						</Button>
					</Stack>
				</Paper>

				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "rgba(16,185,129,0.03)" }}>
					<Stack spacing={1.25}>
						<Box>
							<Typography variant="subtitle2" fontWeight={700}>
								{uploadTitle}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{uploadDescription}
							</Typography>
						</Box>
						{selectedFileName ? (
							<Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
								Selected File: {selectedFileName}
							</Typography>
						) : null}
						<Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
							<Button
								component="label"
								variant="outlined"
								startIcon={<UploadFileIcon />}
								disabled={readOnlyUploadDisabled}
								size="small"
								sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
								Select CSV
								<input hidden accept=".csv,text/csv" type="file" onChange={onFileSelect} disabled={readOnlyUploadDisabled} />
							</Button>
							<Button
								variant="contained"
								onClick={onUpload}
								disabled={uploadDisabled || readOnlyUploadDisabled}
								size="small"
								sx={{ height: 38, px: 2, alignSelf: "flex-start" }}>
								{uploadLabel}
							</Button>
						</Stack>
					</Stack>
				</Paper>
			</Box>
		</Stack>
	</Paper>
);

const ReportTableCard = ({ title, rows, loading }) => {
	const columns = useMemo(() => buildColumnsFromRows(rows), [rows]);

	return (
		<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
			<Stack spacing={1.25}>
				<Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
					<Typography variant="subtitle1" fontWeight={700}>
						{title}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Rows: {rows.length.toLocaleString("en-IN")}
					</Typography>
				</Stack>

				<Box sx={{ height: { xs: "calc(100vh - 360px)", md: "calc(100vh - 310px)" }, minHeight: 360, width: "100%" }}>
					<DataGrid
						rows={rows}
						columns={columns}
						loading={loading}
						slots={{ toolbar: ReportToolbar }}
						density="compact"
						disableRowSelectionOnClick
						pageSizeOptions={[25, 50, 100]}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 25, page: 0 },
							},
						}}
						sx={{
							border: "none",
							fontSize: 13,
							"& .MuiDataGrid-row": { minHeight: 36, maxHeight: 36 },
							"& .MuiDataGrid-cell": { py: 0.5 },
							"& .MuiDataGrid-columnHeaders": {
								backgroundColor: "#f5f5f5",
								minHeight: 40,
								maxHeight: 40,
							},
						}}
					/>
				</Box>
			</Stack>
		</Paper>
	);
};

const SalarySection = ({
	rows,
	selectedFranchise,
	selectedFile,
	selectedFileName,
	loading,
	onDownload,
	onUpload,
	onFileSelect,
	disabled = false,
}) => (
	<Stack spacing={1.5}>
		{rows.length === 0 ? (
			<CsvActionsCard
				title="Store Salary CSV"
				description="Download the franchise salary CSV, update earned salary and liability values, then upload the updated file."
				downloadTitle="Download Salary CSV"
				downloadDescription="CSV columns: franchise name, store name, store code, earned salary, incentive, employer ESI, employer PF, total liability."
				downloadLabel="Download CSV"
				onDownload={onDownload}
				downloadDisabled={!selectedFranchise}
				uploadTitle="Upload Salary CSV"
				uploadDescription="Upload the updated salary CSV for the selected month and franchise."
				uploadLabel="Upload Salary"
				onUpload={onUpload}
				onFileSelect={onFileSelect}
				selectedFileName={selectedFileName}
				uploadDisabled={!selectedFranchise || !selectedFile || loading}
				readOnlyUploadDisabled={disabled}
			/>
		) : null}

		{rows.length > 0 ? <ReportTableCard title="Monthly Store Salary" rows={rows} loading={loading} /> : null}
	</Stack>
);

const PettyCashSection = ({
	rows,
	selectedFranchise,
	selectedFile,
	selectedFileName,
	loading,
	onDownload,
	onUpload,
	onFileSelect,
	disabled = false,
}) => (
	<Stack spacing={1.5}>
		{rows.length === 0 ? (
			<CsvActionsCard
				title="Petty Cash CSV"
				description="Download the franchise petty cash CSV, update amount values, then upload the updated file."
				downloadTitle="Download Petty Cash CSV"
				downloadDescription="CSV columns: franchise name, store name, store code, amount."
				downloadLabel="Download CSV"
				onDownload={onDownload}
				downloadDisabled={!selectedFranchise}
				uploadTitle="Upload Petty Cash CSV"
				uploadDescription="Upload the updated petty cash CSV for the selected month and franchise."
				uploadLabel="Upload Petty Cash"
				onUpload={onUpload}
				onFileSelect={onFileSelect}
				selectedFileName={selectedFileName}
				uploadDisabled={!selectedFranchise || !selectedFile || loading}
				readOnlyUploadDisabled={disabled}
			/>
		) : null}

		{rows.length > 0 ? <ReportTableCard title="Monthly Petty Cash" rows={rows} loading={loading} /> : null}
	</Stack>
);

function SalaryPettyCash() {
	const [tab, setTab] = useState("salary");
	const [monthYear, setMonthYear] = useState(dayjs().startOf("month"));
	const [franchiseOptions, setFranchiseOptions] = useState([]);
	const [selectedFranchise, setSelectedFranchise] = useState("");
	const [reportRows, setReportRows] = useState({ salary: [], petty_cash: [] });
	const [salaryFile, setSalaryFile] = useState(null);
	const [salaryFileName, setSalaryFileName] = useState("");
	const [pettyCashFile, setPettyCashFile] = useState(null);
	const [pettyCashFileName, setPettyCashFileName] = useState("");
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);

	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { user } = AuthState();
	const roleAccessData = roleAccess(user);
	const isReadOnlyUser = ["VIEWER", "EDITOR"].includes(roleAccessData?.roles);

	useEffect(() => {
		const fetchMappedFranchises = async () => {
			try {
				setLoadingOpen(true);
				const res = await pythonVmInstance({
					url: "/ebo-payment-working/cofo/mapped-stores",
					headers: { "Content-Type": "application/json" },
					method: "GET",
				});
				const rows = getResponseRows(res?.data);
				setFranchiseOptions(rows);
				setSelectedFranchise((current) => current || rows[0]?.franchise_name || "");
			} catch (fetchError) {
				setFranchiseOptions([]);
				setSelectedFranchise("");
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: fetchError?.response?.data?.message || "Failed to fetch COFO franchise list",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			} finally {
				setLoadingOpen(false);
			}
		};

		fetchMappedFranchises();
	}, []);

	const activeFranchise = useMemo(
		() => franchiseOptions.find((option) => option.franchise_name === selectedFranchise) || null,
		[franchiseOptions, selectedFranchise]
	);

    console.log("activeFranchise12345678", activeFranchise)
	const salaryRows = useMemo(
		() => buildSalaryRows(getResponseRows(reportRows?.salary), monthYear, activeFranchise?.franchise_name),
		[activeFranchise?.franchise_name, monthYear, reportRows]
	);

	const salaryTemplateRows = useMemo(
		() => buildSalaryTemplateRows(activeFranchise),
		[activeFranchise, salaryRows]
	);

	const pettyCashRows = useMemo(
		() => buildPettyCashRows(getResponseRows(reportRows?.petty_cash), monthYear, activeFranchise?.franchise_name || ""),
		[activeFranchise?.franchise_name, monthYear, reportRows]
	);
	const pettyCashTemplateRows = useMemo(
		() => buildPettyCashTemplateRows(activeFranchise),
		[activeFranchise, pettyCashRows]
	);

    console.log("activeFranchise", activeFranchise)

	const fetchReportData = async ({ showValidationError = false } = {}) => {
		if (!activeFranchise?.franchise_name || !monthYear || !dayjs(monthYear).isValid()) {
			if (showValidationError) {
				setError(!activeFranchise?.franchise_name ? "Please select a franchise." : "Please select a valid month.");
			}
			return;
		}
		try {
			setLoading(true);
			setLoadingOpen(true);
			setError("");
			setSuccess("");
			setHasFetched(true);
			const startDate = dayjs(monthYear).startOf("month").format("YYYY-MM-DD");
			const endDate = dayjs(monthYear).endOf("month").format("YYYY-MM-DD");
			const res = await pythonVmInstance({
					url: "/ebo-payment-working/cofo/salary-petty-cash",
					headers: { "Content-Type": "application/json" },
                    params: {
                        franchise_name: activeFranchise?.franchise_name || "",
                        month: startDate,
                    },
					method: "GET",
				});
			setReportRows({
				salary: getResponseRows(res?.data?.salary),
				petty_cash: getResponseRows(res?.data?.petty_cash),
			});
		} catch (fetchError) {
			setReportRows({ salary: [], petty_cash: [] });
			setError(fetchError?.response?.data?.message || "Failed to fetch COFO report data.");
		} finally {
			setLoading(false);
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		if (!activeFranchise?.franchise_name || !monthYear || !dayjs(monthYear).isValid()) return;
		fetchReportData();
	}, [activeFranchise?.franchise_name, monthYear]);

	const handleGetReport = async () => {
		await fetchReportData({ showValidationError: true });
	};

	const handleSalaryDownload = () => {
		if (!activeFranchise) {
			setError("Please select a franchise.");
			return;
		}

		const rows = salaryTemplateRows;
		if (!rows.length) {
			setError("No stores found for the selected franchise.");
			return;
		}

		const safeName = String(activeFranchise.franchise_name || "cofo_salary")
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "_");

		downloadCsv(rows, `${safeName}_salary_${dayjs(monthYear).format("MMM_YYYY").toLowerCase()}.csv`, [
			"franchise_name",
			"store_name",
			"store_code",
			"earned_salary",
			"incentive",
			"employer_esi",
			"employer_pf",
			"total_liability",
		]);
	};

	const handleFileSelect = (type) => (event) => {
		const file = event.target.files?.[0] || null;
		event.target.value = "";
		if (!file) return;
		if (!file.name.toLowerCase().endsWith(".csv")) {
			if (type === "salary") {
				setSalaryFile(null);
				setSalaryFileName("");
			} else {
				setPettyCashFile(null);
				setPettyCashFileName("");
			}
			setError("Please select a CSV file.");
			return;
		}

		if (type === "salary") {
			setSalaryFile(file);
			setSalaryFileName(file.name);
		} else {
			setPettyCashFile(file);
			setPettyCashFileName(file.name);
		}
		setError("");
		setSuccess("");
	};

	const handleUpload = async ({ type, file, onSuccess, clearFile }) => {
		if (!activeFranchise) {
			setError("Please select a franchise.");
			return;
		}
		if (!monthYear || !dayjs(monthYear).isValid()) {
			setError("Please select a valid month.");
			return;
		}
		if (!file) {
			setError("Please select a CSV file to upload.");
			return;
		}

		try {
			setLoading(true);
			setLoadingOpen(true);
			setError("");
			setSuccess("");
			const form = new FormData();
			form.append("files", file);
			form.append("email", user?.email || "");
			form.append("month", dayjs(monthYear).format("YYYY-MM-DD"));
			form.append("type", type);
			form.append("franchise_name", activeFranchise?.franchise_name || "");
			form.append("store_type", "cofo");

			await pythonVmInstance.post("/ebo-payment-working/cofo/upload-salary-petty-cash", form, {
				headers: { "Content-Type": "multipart/form-data" },
			});

			setSuccess(onSuccess);
			clearFile();
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: onSuccess,
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			await fetchReportData();
		} catch (uploadError) {
			setError(uploadError?.response?.data?.message || `Failed to upload ${type === "salary" ? "salary" : "petty cash"} CSV.`);
		} finally {
			setLoading(false);
			setLoadingOpen(false);
		}
	};

	const handleSalaryUpload = async () => {
		await handleUpload({
			type: "salary",
			file: salaryFile,
			onSuccess: "Salary CSV uploaded successfully.",
			clearFile: () => {
				setSalaryFile(null);
				setSalaryFileName("");
			},
		});
	};

	const handlePettyCashDownload = () => {
		if (!activeFranchise) {
			setError("Please select a franchise.");
			return;
		}

		const rows = pettyCashTemplateRows;
		if (!rows.length) {
			setError("No stores found for the selected franchise.");
			return;
		}

		const safeName = String(activeFranchise.franchise_name || "cofo_petty_cash")
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "_");

		downloadCsv(rows, `${safeName}_petty_cash_${dayjs(monthYear).format("MMM_YYYY").toLowerCase()}.csv`, [
			"franchise_name",
			"store_name",
			"store_code",
			"amount",
		]);
	};

	const handlePettyCashUpload = async () => {
		await handleUpload({
			type: "petty_cash",
			file: pettyCashFile,
			onSuccess: "Petty cash CSV uploaded successfully.",
			clearFile: () => {
				setPettyCashFile(null);
				setPettyCashFileName("");
			},
		});
	};

	return (
		<Box width="100%" p={1.5} sx={{ background: "linear-gradient(180deg, #fafafa 0%, #ffffff 100%)" }}>
			<Stack spacing={1.5}>
				<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
					<Stack spacing={1.5}>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								COFO Monthly Store Report
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
								Choose a month and franchise to manage salary sheets or review petty cash franchise-wise.
							</Typography>
						</Box>

						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									gap: 1.25,
									alignItems: "center",
									"& > *": {
										flex: "1 1 180px",
										minWidth: { xs: "100%", sm: "180px" },
									},
								}}>
								<DatePicker
									views={["year", "month"]}
									openTo="month"
									label="Month-Year"
									value={monthYear}
									onChange={(newValue) => {
										setMonthYear(newValue);
										setError("");
										setSuccess("");
									}}
									maxDate={dayjs().endOf("month")}
									slotProps={{
										textField: {
											size: "small",
											sx: {
												width: "100%",
												"& .MuiInputBase-root": { height: 38, borderRadius: 1.5 },
												"& .MuiInputBase-input": { py: 0.85, fontSize: 13 },
											},
										},
									}}
									format="MMM YYYY"
								/>

								<TextField
									select
									size="small"
									label="Select Franchise"
									value={selectedFranchise}
									onChange={(event) => {
										setSelectedFranchise(event.target.value);
										setError("");
										setSuccess("");
									}}
									sx={{
										width: "100%",
										"& .MuiInputBase-root": { height: 38, borderRadius: 1.5 },
										"& .MuiInputBase-input": { py: 0.85, fontSize: 13 },
									}}>
									{franchiseOptions.length === 0 ? (
										<MenuItem value="" disabled>
											No franchise available
										</MenuItem>
									) : (
										franchiseOptions.map((option) => (
											<MenuItem key={`${option.franchise_name}-${option.franchise_name}`} value={option.franchise_name}>
												{option.franchise_name}
											</MenuItem>
										))
									)}
								</TextField>

								<Button
									variant="contained"
									onClick={handleGetReport}
									disabled={loading || !selectedFranchise || !monthYear}
									size="small"
									sx={{ height: 38, px: 2, minWidth: { xs: "100%", sm: 120 }, flex: "0 0 auto" }}>
									{loading ? "Fetching..." : "Get Report"}
								</Button>
							</Box>
						</LocalizationProvider>

						<Tabs value={tab} onChange={(_, nextValue) => setTab(nextValue)} sx={{ minHeight: 42 }}>
							<Tab value="salary" label="Store Salary" sx={{ minHeight: 42 }} />
							<Tab value="petty_cash" label="Petty Cash" sx={{ minHeight: 42 }} />
						</Tabs>
						{error ? <Alert severity="error">{error}</Alert> : null}
						{success ? <Alert severity="success">{success}</Alert> : null}
						{!hasFetched ? (
							<Alert severity="info">Select a month and franchise, then click Get Report to load store data.</Alert>
						) : null}
						{hasFetched && !error && !loading && pettyCashRows.length === 0 && tab === "petty_cash" ? (
							<Alert severity="warning">No records found for the selected franchise and month.</Alert>
						) : null}
					</Stack>
				</Paper>

				{tab === "salary" ? (
					<SalarySection
						rows={salaryRows}
						selectedFranchise={selectedFranchise}
						selectedFile={salaryFile}
						selectedFileName={salaryFileName}
						loading={loading}
						onDownload={handleSalaryDownload}
						onUpload={handleSalaryUpload}
						onFileSelect={handleFileSelect("salary")}
						disabled={isReadOnlyUser}
					/>
				) : (
					<PettyCashSection
						rows={pettyCashRows}
						selectedFranchise={selectedFranchise}
						selectedFile={pettyCashFile}
						selectedFileName={pettyCashFileName}
						loading={loading}
						onDownload={handlePettyCashDownload}
						onUpload={handlePettyCashUpload}
						onFileSelect={handleFileSelect("petty_cash")}
						disabled={isReadOnlyUser}
					/>
				)}
			</Stack>
		</Box>
	);
}

export default SalaryPettyCash;
